import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Plus, CheckCircle2, Loader2, Upload, X, MapPin, ArrowLeft, ArrowRight,
  Building2, Palette, Phone, Globe, Instagram, Facebook, Clock, Mail,
} from 'lucide-react';
import { optimizeImageForUpload, LOGO_PRESET, PHOTO_PRESET } from '@/lib/imageOptimize';
import type { BusinessEntry, WizardMode } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import { BrandLogoUploader } from '@/components/BrandLogoUploader';

// ─── Constants ──────────────────────────────────────────────────────────────

// Kept in sync with the seeded categories in the directory expansion
// migration (`20260620560000_seed_dominica_directory_expansion.sql`).
// Underscore-separated values match the `local_business_listings.category`
// column. The `replace(/_/g, ' ')` in the dropdown labels turns them into
// human-readable text ("gas_station" → "gas station").
const CATEGORIES = [
  'hotel', 'guesthouse', 'resort',
  'restaurant', 'cafe', 'bakery', 'bar', 'nightclub', 'distillery',
  'supermarket', 'convenience_store', 'retail', 'boutique', 'pharmacy',
  'spa', 'salon', 'gym', 'medical',
  'bank', 'telecom', 'media',
  'auto', 'gas_station',
  'attraction', 'tour_operator', 'watersports', 'gallery',
  'education', 'utility', 'event_venue', 'festival',
  'government', 'tourism_board',
  'law_firm', 'funeral_home',
  'other',
] as const;

const PARISHES = [
  'Saint Andrew', 'Saint David', 'Saint George', 'Saint John', 'Saint Joseph',
  'Saint Luke', 'Saint Mark', 'Saint Patrick', 'Saint Paul', 'Saint Peter',
] as const;

// ─── Props ──────────────────────────────────────────────────────────────────

interface BrandPickerProps {
  mode: WizardMode;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function BrandPicker({ mode }: BrandPickerProps) {
  const store = useWizardStore(mode);
  const { selectedBusiness, newBusinessOpen, newBusinessStep } = store;

  if (selectedBusiness && !newBusinessOpen) {
    return <SelectedBusinessCard mode={mode} />;
  }

  if (newBusinessOpen) {
    return <NewBusinessFlow mode={mode} />;
  }

  return <BusinessSearch mode={mode} />;
}

// ─── Selected Business Card ─────────────────────────────────────────────────

function SelectedBusinessCard({ mode }: { mode: WizardMode }) {
  const store = useWizardStore(mode);
  const b = store.selectedBusiness!;

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
      <div className="flex items-center gap-4">
        {b.logo_url ? (
          <img src={b.logo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-gray-600" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
            <span className="text-xl font-bold text-emerald-400">{b.name.charAt(0)}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-semibold text-lg truncate">{b.name}</h3>
            {b.is_verified && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
          </div>
          {b.slogan && <p className="text-sm text-gray-400 truncate">{b.slogan}</p>}
          <div className="flex items-center gap-2 mt-1">
            {b.category && (
              <Badge variant="outline" className="text-xs border-gray-600 text-gray-300 capitalize">
                {b.category.replace(/_/g, ' ')}
              </Badge>
            )}
            {b.village && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {b.village}{b.parish ? `, ${b.parish}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Fix-the-fallback uploader: only shown when this directory
              brand is on the letter avatar. Patches logo_url on the
              listing row so every future render (wizard pill, gallery,
              in-app pin card) picks it up automatically. */}
          {!b.logo_url && (
            <BrandLogoUploader
              businessId={b.id}
              businessName={b.name}
              currentLogoUrl={b.logo_url}
              onUploaded={(publicUrl) => {
                store.set({
                  selectedBusiness: { ...b, logo_url: publicUrl },
                  gallerySelectedLogoUrl: publicUrl,
                });
              }}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
            onClick={() => store.set({ selectedBusiness: null })}
          >
            Change
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Business Search ────────────────────────────────────────────────────────

function BusinessSearch({ mode }: { mode: WizardMode }) {
  const store = useWizardStore(mode);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BusinessEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('search_business_directory', {
        p_query: q,
        p_limit: 8,
      });
      if (error) throw error;
      setResults((data as BusinessEntry[]) || []);
    } catch (err: any) {
      console.error('Business search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => search(query), 200);
    } else {
      setResults([]);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectBusiness = (b: BusinessEntry) => {
    // Capture the current step BEFORE selectBusiness runs (it sets step:1
    // unconditionally for the legacy sponsor flow). For festival mode the
    // brand picker is on step 1 itself, so we want to advance to step 2
    // (Creative) instead of staying put. Compute the target first, then
    // overwrite the step the store action set.
    const fromStep = store.step;
    store.selectBusiness(b);
    store.set({ step: Math.max(fromStep + 1, 1) });
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Select a business</h2>
        <p className="text-sm text-gray-400">Search the directory or add a new business entry.</p>
      </div>

      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search Fort Young, Pearl's Cuisine, KFC, Digicel…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            className="pl-9 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-11"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />}
        </div>

        {open && (query.length >= 2 || results.length > 0) && (
          <div className="absolute z-50 w-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-[380px] overflow-y-auto">
            {results.length === 0 && !loading && query.length >= 2 && (
              <div className="p-4 text-center text-gray-500 text-sm">
                No businesses found for "{query}"
              </div>
            )}

            {results.map((b) => (
              <button
                key={b.id}
                className="w-full text-left px-3 py-2.5 hover:bg-gray-800/70 transition-colors flex items-center gap-3 border-b border-gray-800/50 last:border-0"
                onClick={() => selectBusiness(b)}
              >
                {b.logo_url ? (
                  <img src={b.logo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-gray-700 shrink-0" />
                ) : (
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 border border-gray-700"
                    style={{ backgroundColor: b.brand_color_primary || '#1a4a3a' }}
                  >
                    <span className="text-sm font-bold text-white">{b.name.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-white font-medium text-sm truncate">{b.name}</span>
                    {b.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                  </div>
                  {b.slogan && <p className="text-xs text-gray-500 truncate">{b.slogan}</p>}
                  <div className="flex items-center gap-2 mt-0.5">
                    {b.category && (
                      <span className="text-[10px] text-gray-500 capitalize">{b.category.replace(/_/g, ' ')}</span>
                    )}
                    {b.village && (
                      <span className="text-[10px] text-gray-600">{b.village}{b.parish ? `, ${b.parish}` : ''}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}

            {/* Always show "+ Add new business" at bottom */}
            <button
              className="w-full text-left px-3 py-3 hover:bg-emerald-500/10 transition-colors flex items-center gap-3 border-t border-gray-700"
              onClick={() => {
                store.set({ newBusinessOpen: true, newBusinessStep: 0 });
                setOpen(false);
              }}
            >
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <Plus className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <span className="text-emerald-400 font-medium text-sm">+ Add new business</span>
                <p className="text-xs text-gray-500">Create a directory entry for a new brand</p>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Quick add button when search is empty */}
      {query.length < 2 && (
        <button
          className="w-full text-left p-3 rounded-lg border border-dashed border-gray-700 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors flex items-center gap-3"
          onClick={() => store.set({ newBusinessOpen: true, newBusinessStep: 0 })}
        >
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <Plus className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <span className="text-emerald-400 font-medium text-sm">+ Add new business</span>
            <p className="text-xs text-gray-500">Not in the directory yet? Create a new entry.</p>
          </div>
        </button>
      )}
    </div>
  );
}

// ─── New Business Flow ──────────────────────────────────────────────────────

function NewBusinessFlow({ mode }: { mode: WizardMode }) {
  const store = useWizardStore(mode);
  const { newBusinessStep } = store;

  // Local form state for the new business
  const [form, setForm] = useState({
    name: '', slug: '', category: '', slogan: '', description: '',
    logo_url: '', hero_image_url: '',
    brand_color_primary: '#0f3d2e', brand_color_accent: '#10b981',
    latitude: 15.301, longitude: -61.388,
    address_line: '', village: '', parish: '',
    phone: '', email: '', website_url: '', whatsapp: '',
    facebook_url: '', instagram_handle: '', hours_summary: '',
  });
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);

  // Auto-slug from name
  useEffect(() => {
    const slug = form.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    setForm((f) => ({ ...f, slug }));
  }, [form.name]);

  const updateField = (field: string, value: string | number) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const uploadFile = async (file: File, bucket: string, setUploading: (v: boolean) => void, field: string) => {
    setUploading(true);
    try {
      const optimized = await optimizeImageForUpload(
        file,
        field === 'logo_url' ? LOGO_PRESET : PHOTO_PRESET,
      );
      const ext = optimized.name.split('.').pop() || 'png';
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from(bucket).upload(path, optimized, {
        contentType: optimized.type,
        upsert: false,
      });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
      setForm((f) => ({ ...f, [field]: urlData.publicUrl }));
      toast.success(`${field === 'logo_url' ? 'Logo' : 'Hero image'} uploaded`);
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Business name is required'); return; }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('upsert_business_directory_entry', {
        p_name: form.name.trim(),
        p_slug: form.slug || null,
        p_category: form.category || null,
        p_slogan: form.slogan || null,
        p_description: form.description || null,
        p_logo_url: form.logo_url || null,
        p_hero_image_url: form.hero_image_url || null,
        p_brand_color_primary: form.brand_color_primary || null,
        p_brand_color_accent: form.brand_color_accent || null,
        p_latitude: form.latitude,
        p_longitude: form.longitude,
        p_address_line: form.address_line || null,
        p_village: form.village || null,
        p_parish: form.parish || null,
        p_phone: form.phone || null,
        p_email: form.email || null,
        p_website_url: form.website_url || null,
        p_whatsapp: form.whatsapp || null,
        p_facebook_url: form.facebook_url || null,
        p_instagram_handle: form.instagram_handle || null,
        p_hours_summary: form.hours_summary || null,
      });
      if (error) throw error;

      // upsert_business_directory_entry returns { ok, created, business: <row>, error? }
      const wrapper = (data as any) || {};
      if (wrapper.ok === false) {
        throw new Error(wrapper.error || 'Failed to save business');
      }
      const entry = wrapper.business || wrapper; // tolerate both shapes
      if (!entry?.id) {
        throw new Error('Server did not return a business id. Check the RLS policy.');
      }

      const business: BusinessEntry = {
        id: entry.id,
        name: entry.name || form.name,
        slug: entry.slug || form.slug,
        category: entry.category || form.category,
        slogan: entry.slogan || form.slogan,
        description: entry.description || form.description,
        logo_url: entry.logo_url || form.logo_url,
        hero_image_url: entry.hero_image_url || form.hero_image_url,
        brand_color_primary: entry.brand_color_primary || form.brand_color_primary,
        brand_color_accent: entry.brand_color_accent || form.brand_color_accent,
        latitude: entry.latitude ?? form.latitude,
        longitude: entry.longitude ?? form.longitude,
        address_line: entry.address_line || form.address_line,
        village: entry.village || form.village,
        parish: entry.parish || form.parish,
        phone: entry.phone || form.phone,
        email: entry.email || form.email,
        website_url: entry.website_url || form.website_url,
        whatsapp: entry.whatsapp || form.whatsapp,
        facebook_url: entry.facebook_url || form.facebook_url,
        instagram_handle: entry.instagram_handle || form.instagram_handle,
        hours_summary: entry.hours_summary || form.hours_summary,
        is_verified: entry.is_verified ?? false,
        is_featured: entry.is_featured ?? false,
      };

      store.set({ newBusinessOpen: false, newBusinessStep: 0 });
      const fromStep = store.step;
      store.selectBusiness(business);
      store.set({ step: Math.max(fromStep + 1, 1) });
      toast.success(
        wrapper.created === false
          ? `${business.name} updated in directory`
          : `${business.name} added to directory`,
      );
    } catch (err: any) {
      toast.error(err.message || 'Failed to save business');
    } finally {
      setSaving(false);
    }
  };

  const steps = ['Identity', 'Brand', 'Location & Contact'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-400 hover:text-white w-8 h-8"
            onClick={() => store.set({ newBusinessOpen: false })}
          >
            <X className="w-4 h-4" />
          </Button>
          <h2 className="text-lg font-semibold text-white">New business</h2>
        </div>
        <div className="flex items-center gap-1.5">
          {steps.map((s, i) => (
            <button
              key={s}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                i === newBusinessStep
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : i < newBusinessStep
                  ? 'bg-gray-700/50 text-gray-300'
                  : 'bg-gray-800/50 text-gray-500'
              }`}
              onClick={() => store.set({ newBusinessStep: i })}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Step content */}
      {newBusinessStep === 0 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Business name *</Label>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className="bg-gray-900 border-gray-700 text-white"
              placeholder="e.g. Fort Young Hotel"
              autoFocus
            />
            {form.slug && (
              <p className="text-xs text-gray-500 font-mono">slug: {form.slug}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Category</Label>
            <Select value={form.category} onValueChange={(v) => updateField('category', v)}>
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue placeholder="Select category…" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="text-gray-200 capitalize">
                    {c.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Slogan / tagline</Label>
            <Input
              value={form.slogan}
              onChange={(e) => updateField('slogan', e.target.value)}
              className="bg-gray-900 border-gray-700 text-white"
              placeholder="e.g. Dominica's premier waterfront hotel"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              className="bg-gray-900 border-gray-700 text-white min-h-[80px]"
              placeholder="One paragraph about the business…"
            />
          </div>
        </div>
      )}

      {newBusinessStep === 1 && (
        <div className="space-y-4">
          {/* Logo upload */}
          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Logo (square recommended)
            </Label>
            <div className="flex items-center gap-4">
              {form.logo_url ? (
                <div className="relative">
                  <img src={form.logo_url} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-gray-600" />
                  <button
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center"
                    onClick={() => setForm((f) => ({ ...f, logo_url: '' }))}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full border-2 border-dashed border-gray-600 hover:border-emerald-500/50 flex flex-col items-center justify-center cursor-pointer transition-colors">
                  {logoUploading ? (
                    <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-gray-500" />
                      <span className="text-[10px] text-gray-500 mt-1">Upload</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
                        uploadFile(file, 'business-logos', setLogoUploading, 'logo_url');
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Hero image upload */}
          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-2">
              <Palette className="w-4 h-4" /> Hero image (wide aspect, optional)
            </Label>
            {form.hero_image_url ? (
              <div className="relative">
                <img src={form.hero_image_url} alt="" className="w-full h-32 object-cover rounded-lg border border-gray-700" />
                <button
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500/90 flex items-center justify-center"
                  onClick={() => setForm((f) => ({ ...f, hero_image_url: '' }))}
                >
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            ) : (
              <label className="block w-full h-28 rounded-lg border-2 border-dashed border-gray-600 hover:border-emerald-500/50 flex items-center justify-center cursor-pointer transition-colors">
                {heroUploading ? (
                  <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-5 h-5 text-gray-500" />
                    <span className="text-xs text-gray-500 mt-1">Drop or click (max 5 MB)</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) { toast.error('Hero image must be under 5 MB'); return; }
                      uploadFile(file, 'business-hero-images', setHeroUploading, 'hero_image_url');
                    }
                  }}
                />
              </label>
            )}
          </div>

          {/* Brand colors */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Primary brand color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.brand_color_primary}
                  onChange={(e) => updateField('brand_color_primary', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-700 cursor-pointer bg-transparent"
                />
                <Input
                  value={form.brand_color_primary}
                  onChange={(e) => updateField('brand_color_primary', e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white font-mono text-sm flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Accent color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.brand_color_accent}
                  onChange={(e) => updateField('brand_color_accent', e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-700 cursor-pointer bg-transparent"
                />
                <Input
                  value={form.brand_color_accent}
                  onChange={(e) => updateField('brand_color_accent', e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white font-mono text-sm flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {newBusinessStep === 2 && (
        <div className="space-y-4">
          {/* Leaflet map */}
          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Coordinates (drag the pin)
            </Label>
            <LeafletPicker
              lat={form.latitude}
              lng={form.longitude}
              onMove={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">Latitude</Label>
                <Input value={form.latitude.toFixed(6)} readOnly className="bg-gray-800 border-gray-700 text-gray-400 text-sm font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-500 text-xs">Longitude</Label>
                <Input value={form.longitude.toFixed(6)} readOnly className="bg-gray-800 border-gray-700 text-gray-400 text-sm font-mono" />
              </div>
            </div>
            <a
              href={`https://www.google.com/maps?q=${encodeURIComponent(form.name + ' Dominica')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-emerald-400 hover:underline flex items-center gap-1"
            >
              <Globe className="w-3 h-3" /> Search Google Maps for reference
            </a>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label className="text-gray-300">Address line</Label>
            <Input
              value={form.address_line}
              onChange={(e) => updateField('address_line', e.target.value)}
              className="bg-gray-900 border-gray-700 text-white"
              placeholder="e.g. Victoria Street, Roseau"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300">Village</Label>
              <Input
                value={form.village}
                onChange={(e) => updateField('village', e.target.value)}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="e.g. Roseau"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Parish</Label>
              <Select value={form.parish} onValueChange={(v) => updateField('parish', v)}>
                <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {PARISHES.map((p) => (
                    <SelectItem key={p} value={p} className="text-gray-200">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contact fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
              <Input value={form.phone} onChange={(e) => updateField('phone', e.target.value)} className="bg-gray-900 border-gray-700 text-white" placeholder="+1 767 …" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-1"><Phone className="w-3 h-3" /> WhatsApp</Label>
              <Input value={form.whatsapp} onChange={(e) => updateField('whatsapp', e.target.value)} className="bg-gray-900 border-gray-700 text-white" placeholder="+1 767 …" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
              <Input value={form.email} onChange={(e) => updateField('email', e.target.value)} className="bg-gray-900 border-gray-700 text-white" placeholder="info@…" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-1"><Globe className="w-3 h-3" /> Website</Label>
              <Input value={form.website_url} onChange={(e) => updateField('website_url', e.target.value)} className="bg-gray-900 border-gray-700 text-white" placeholder="https://…" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-1"><Facebook className="w-3 h-3" /> Facebook URL</Label>
              <Input value={form.facebook_url} onChange={(e) => updateField('facebook_url', e.target.value)} className="bg-gray-900 border-gray-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-1"><Instagram className="w-3 h-3" /> Instagram</Label>
              <Input value={form.instagram_handle} onChange={(e) => updateField('instagram_handle', e.target.value)} className="bg-gray-900 border-gray-700 text-white" placeholder="@handle" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-1"><Clock className="w-3 h-3" /> Hours summary</Label>
            <Input value={form.hours_summary} onChange={(e) => updateField('hours_summary', e.target.value)} className="bg-gray-900 border-gray-700 text-white" placeholder="Mon–Sat 8am–6pm" />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          className="text-gray-400 hover:text-white"
          onClick={() => {
            if (newBusinessStep === 0) {
              store.set({ newBusinessOpen: false });
            } else {
              store.set({ newBusinessStep: newBusinessStep - 1 });
            }
          }}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {newBusinessStep === 0 ? 'Back to search' : 'Back'}
        </Button>

        {newBusinessStep < 2 ? (
          <Button
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            disabled={newBusinessStep === 0 && !form.name.trim()}
            onClick={() => store.set({ newBusinessStep: newBusinessStep + 1 })}
          >
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            disabled={saving || !form.name.trim()}
            onClick={handleSave}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save & continue
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Leaflet Picker ─────────────────────────────────────────────────────────

function LeafletPicker({ lat, lng, onMove }: { lat: number; lng: number; onMove: (lat: number, lng: number) => void }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Dynamically import leaflet to avoid SSR issues
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');

      if (cancelled || !mapRef.current || mapInstanceRef.current) return;

      const map = L.map(mapRef.current, {
        center: [lat, lng],
        zoom: 13,
        scrollWheelZoom: true,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);

      // Custom icon
      const icon = L.divIcon({
        html: `<div style="width:32px;height:32px;background:#10b981;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        </div>`,
        className: '',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      });

      const marker = L.marker([lat, lng], { draggable: true, icon }).addTo(map);

      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        onMove(Math.round(pos.lat * 1e6) / 1e6, Math.round(pos.lng * 1e6) / 1e6);
      });

      mapInstanceRef.current = map;
      markerRef.current = marker;
      setReady(true);

      // Force a resize after mount
      setTimeout(() => map.invalidateSize(), 100);
    }

    init();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker position when lat/lng change externally
  useEffect(() => {
    if (markerRef.current && ready) {
      const pos = markerRef.current.getLatLng();
      if (Math.abs(pos.lat - lat) > 0.0001 || Math.abs(pos.lng - lng) > 0.0001) {
        markerRef.current.setLatLng([lat, lng]);
        mapInstanceRef.current?.panTo([lat, lng]);
      }
    }
  }, [lat, lng, ready]);

  return (
    <div
      ref={mapRef}
      className="w-full h-[220px] rounded-lg border border-gray-700 overflow-hidden"
      style={{ background: '#1a1a2e' }}
    />
  );
}
